// Conséquences locales d'une écriture sur les autres entités (affectation d'un
// camion, statut de trajet, stock…), pour que les écrans voisins suivent comme
// en ligne. Les règles sont celles du serveur (ports vérifiés par les jeux de
// données communs); un effet inconnu ne fait rien: la synchronisation ramène la
// version du serveur, seule référence.
import { db } from '../../db';
import {
  ecrireEntiteLocale,
  lireEntite,
  lireEntites,
  marquerDerive,
  supprimerEntite,
} from '../store';
import { nouvelIdentifiant } from '../paths';
import {
  calculerProgressionCommande,
  calculerResteApresChangementDeVolume,
  calculerResteTrajet,
  STATUTS_AFFECTABLES,
} from './progressionCommande';
import { calculerEcheanceDuPlan, dernierReleveParCamion, evaluerPapier, evaluerPlan } from './entretien';
import { effetsDuStatutSurLeStock, gestionDepotEffective, reequilibrerVenteDirecte } from './grandLivre';

const effets = {};
export const definirEffetLocal = (nom, fonction) => {
  effets[nom] = fonction;
};

export async function appliquerEffetsLocaux(nom, ctx, donnees) {
  const fonction = effets[nom];
  if (!fonction) return [];
  return (await fonction(ctx, donnees)) || [];
}

const maintenant = () => new Date().toISOString();
const num = (valeur) => {
  const n = Number.parseFloat(valeur);
  return Number.isFinite(n) ? n : 0;
};
const TERMINES = new Set(['COMPLETED', 'CANCELLED']);

// --- Lectures communes -----------------------------------------------------------------------

/** Réglage « la société gère un dépôt »: profil mémorisé, sinon déduction. */
export async function lireGestionDepot(mouvements) {
  const lire = (valeur) => (valeur === undefined || valeur === null ? null : Boolean(valeur));
  try {
    const utilisateur = JSON.parse(
      (typeof localStorage !== 'undefined' && (localStorage.getItem('app_v1_user') || localStorage.getItem('user'))) || 'null',
    );
    const reglage = lire(utilisateur?.entitlements?.manages_depot);
    if (reglage !== null) return reglage;
  } catch (erreur) {
    // profil illisible: on essaie la copie mémorisée
  }
  try {
    const entree = await db.httpCache.get('/users/me/profile');
    const profil = entree?.data?.data ?? entree?.data ?? {};
    const reglage = lire(profil?.entitlements?.manages_depot);
    if (reglage !== null) return reglage;
  } catch (erreur) {
    // retombe sur la déduction
  }
  return gestionDepotEffective(null, mouvements);
}

/** Les lignes d'affectation d'une commande, au format du registre du serveur. */
export function affectationsDeLaCommande(commande) {
  const progression = commande?.progress;
  if (progression?.source === 'REGISTRE' && Array.isArray(progression.trucks)) {
    return progression.trucks.map((camion) => ({
      trip_id: camion.trip_id,
      order_id: commande.id,
      quantity_liters: camion.allocated_liters,
      delivered_quantity: camion.delivered_liters,
    }));
  }
  return null;
}

async function trajetsParId() {
  return Object.fromEntries((await lireEntites('/trips')).map((t) => [String(t.id), t]));
}

/** Recalcule `progress` (et le statut avant départ) d'une commande. */
export async function recalculerCommande(commande, trajets = null) {
  if (!commande) return null;
  const parId = trajets || (await trajetsParId());
  const progression = calculerProgressionCommande(commande, affectationsDeLaCommande(commande), parId);
  const statut = String(commande.status || '').toUpperCase();
  let cible = null;
  if (['DRAFT', 'PENDING', 'CONFIRMED'].includes(statut)) {
    if (progression.allocated_liters > 0 && progression.to_allocate_liters <= 0) cible = 'ALLOCATED';
  } else if (statut === 'ALLOCATED') {
    if (progression.to_allocate_liters > 0 || progression.allocated_liters <= 0) cible = 'CONFIRMED';
  }
  return { ...commande, status: cible || commande.status, progress: progression };
}

async function enregistrerCommande(commande, touches) {
  const recalculee = await recalculerCommande(commande);
  await ecrireEntiteLocale('/orders', recalculee.id, recalculee);
  touches.push({ kind: '/orders', id: recalculee.id });
  return recalculee;
}

/** Affectations actives d'un trajet, toutes commandes confondues. */
async function affectationsDuTrajet(tripId) {
  const lignes = [];
  for (const commande of await lireEntites('/orders')) {
    const registre = affectationsDeLaCommande(commande);
    const source = registre || (commande.allocated_trips || []).map((e) => ({
      trip_id: e.trip_id,
      quantity_liters: e.quantity,
      delivered_quantity: 0,
    }));
    source.forEach((ligne) => {
      if (String(ligne.trip_id) === String(tripId)) lignes.push({ ...ligne, order_id: commande.id });
    });
  }
  return lignes;
}

// Comme le serveur, le volume affecté d'un trajet n'est réécrit qu'à
// l'affectation (registre des allocations); le reste vendable l'est aussi
// quand le volume chargé ou le camion change (voir 'trajet-modifie').
async function enregistrerTrajet(trajet, touches, { recalculer = false } = {}) {
  let complet = { ...trajet, updated_at: maintenant() };
  if (recalculer) {
    const camion = trajet.truck_id ? await lireEntite('/trucks', trajet.truck_id) : null;
    const affectations = await affectationsDuTrajet(trajet.id);
    complet = {
      ...complet,
      allocated_quantity: affectations.reduce((s, a) => s + num(a.quantity_liters), 0) || trajet.allocated_quantity || 0,
      remaining_liters: calculerResteTrajet(trajet, affectations, camion),
    };
  }
  await ecrireEntiteLocale('/trips', complet.id, complet);
  touches.push({ kind: '/trips', id: complet.id });
  return complet;
}

async function appliquerEcrituresStock(resultat, touches) {
  for (const mouvement of resultat.ecritures) {
    await ecrireEntiteLocale('/stock', mouvement.id, { ...mouvement });
    await marquerDerive('/stock', mouvement.id);
    touches.push({ kind: '/stock', id: mouvement.id, derive: true });
  }
  for (const id of resultat.suppressions) {
    await supprimerEntite('/stock', id);
  }
}

// --- Commandes -----------------------------------------------------------------------------

definirEffetLocal('commande-creee', async (ctx, { commande }) => {
  const touches = [];
  const client = commande.client_id ? await lireEntite('/clients', commande.client_id) : null;
  await enregistrerCommande(
    { ...commande, client_name: commande.client_name || client?.name || null },
    touches,
  );
  return touches.slice(1);
});

definirEffetLocal('commande-modifiee', async (ctx, { commande }) => {
  const touches = [];
  await enregistrerCommande(commande, touches);
  return touches.slice(1);
});

definirEffetLocal('affectation', async (ctx, { orderId, tripId, quantite }) => {
  const touches = [];
  const commande = await lireEntite('/orders', orderId);
  const trajet = await lireEntite('/trips', tripId);
  if (!commande || !trajet || !(quantite > 0)) return touches;
  if ((commande.allocated_trips || []).some((e) => String(e.trip_id) === String(tripId))) return touches;
  const horodatage = maintenant();
  const mise = {
    ...commande,
    allocated_trips: [...(commande.allocated_trips || []), { trip_id: tripId, quantity: quantite, allocated_at: horodatage }],
    allocated_trip_ids: [...new Set([...(commande.allocated_trip_ids || []), tripId])],
    progress: commande.progress
      ? {
          ...commande.progress,
          source: 'REGISTRE',
          trucks: [
            ...(commande.progress.trucks || []),
            { trip_id: tripId, plate: trajet.tractor_plate || null, allocated_liters: quantite, delivered_liters: 0, has_left: false },
          ],
        }
      : commande.progress,
  };
  await enregistrerCommande(mise, touches);
  await enregistrerTrajet(
    {
      ...trajet,
      order_id: trajet.order_id || orderId,
      allocated_at: trajet.allocated_at || horodatage,
    },
    touches,
    { recalculer: true },
  );
  return touches;
});

definirEffetLocal('livraison', async (ctx, { orderId, livraison }) => {
  const touches = [];
  const commande = await lireEntite('/orders', orderId);
  const quantite = num(livraison.quantity_liters ?? livraison.delivered_quantity ?? livraison.quantity);
  if (!commande || !(quantite > 0)) return touches;
  const horodatage = livraison.delivery_date || maintenant();
  const identifiant = livraison.id || livraison.delivery_id || nouvelIdentifiant();
  const livre = num(commande.delivered_quantity) + quantite;
  const statut = String(commande.status || '').toUpperCase();
  const progress = commande.progress
    ? {
        ...commande.progress,
        trucks: (commande.progress.trucks || []).map((camion) =>
          String(camion.trip_id) === String(livraison.trip_id)
            ? { ...camion, delivered_liters: num(camion.delivered_liters) + quantite }
            : camion,
        ),
      }
    : commande.progress;
  const mise = {
    ...commande,
    delivered_quantity: livre,
    status: ['DRAFT', 'PENDING', 'CONFIRMED', 'ALLOCATED', 'IN_TRANSIT'].includes(statut)
      ? livre >= num(commande.quantity_liters) ? 'DELIVERED' : 'PARTIALLY_DELIVERED'
      : commande.status,
    delivery_items: [
      ...(commande.delivery_items || []),
      {
        id: identifiant,
        quantity_liters: quantite,
        delivered_quantity: quantite,
        delivery_date: horodatage,
        delivery_location: livraison.delivery_location || commande.delivery_location || null,
        trip_id: livraison.trip_id || null,
        status: 'DELIVERED',
      },
    ],
    progress,
  };
  await enregistrerCommande(mise, touches);

  // La sortie de stock de la livraison (AUTO_DISPATCH), puis la vente directe
  // du trajet ramenée au reste non enregistré.
  const mouvements = await lireEntites('/stock');
  const sortie = {
    id: nouvelIdentifiant(),
    movement_type: 'SALE',
    quantity_liters: quantite,
    unit_cost: commande.unit_cost ?? null,
    total_value: quantite * num(commande.unit_cost),
    from_location: 'DEPOT',
    to_location: 'CLIENT',
    to_location_id: orderId,
    trip_id: livraison.trip_id || null,
    order_id: orderId,
    delivery_id: identifiant,
    movement_date: horodatage,
    source: 'AUTO_DISPATCH',
    created_by: 'SYSTEM',
    organization_id: commande.organization_id ?? null,
    created_at: maintenant(),
  };
  const ecritures = [sortie];
  const suppressions = [];
  if (livraison.trip_id) {
    const trajet = await lireEntite('/trips', livraison.trip_id);
    if (trajet) {
      const doTrajet = [...mouvements.filter((m) => m.trip_id === trajet.id), sortie];
      const charge = num(trajet.fuel_quantity_real) || num(trajet.allocated_quantity) || num(trajet.fuel_quantity_estimated);
      reequilibrerVenteDirecte({
        trajet,
        doTrajet,
        charge,
        ecrire: (m) => {
          const i = ecritures.findIndex((e) => e.id === m.id);
          if (i >= 0) ecritures[i] = m;
          else ecritures.push(m);
        },
        suppressions,
        creer: false,
      });
    }
  }
  await appliquerEcrituresStock({ ecritures, suppressions }, touches);
  return touches.slice(1);
});

// --- Trajets -------------------------------------------------------------------------------

definirEffetLocal('trajet-cree', async (ctx, { trajet }) => {
  const touches = [];
  const camion = trajet.truck_id ? await lireEntite('/trucks', trajet.truck_id) : null;
  const complet = {
    ...trajet,
    tractor_plate: trajet.tractor_plate || camion?.matricule || null,
    tank_plate: trajet.tank_plate || camion?.tank_number || null,
    driver_id: trajet.driver_id || camion?.default_driver_id || camion?.assigned_driver_id || null,
    phase: trajet.phase || 'IMPORTATION',
    remaining_liters: calculerResteTrajet(trajet, [], camion),
  };
  await ecrireEntiteLocale('/trips', complet.id, complet);
  if (camion) {
    await ecrireEntiteLocale('/trucks', camion.id, { ...camion, current_status: 'ON_TRIP', updated_at: maintenant() });
    touches.push({ kind: '/trucks', id: camion.id });
  }
  return touches;
});

definirEffetLocal('trajet-modifie', async (ctx, { trajet, avant }) => {
  const touches = [];
  const volumeChange = 'fuel_quantity_real' in (ctx.corps || {}) && num(avant?.fuel_quantity_real) !== num(trajet.fuel_quantity_real);
  const camionChange = 'truck_id' in (ctx.corps || {}) && String(avant?.truck_id ?? '') !== String(trajet.truck_id ?? '');
  let modifie = { ...trajet, ...(volumeChange ? { fuel_quantity_real_source: 'MANUAL' } : {}) };
  if (volumeChange || camionChange) {
    // Comme le serveur (refresh_trip_remaining): le reste vendable suit tout de
    // suite le volume chargé ou le camion, sans attendre une nouvelle affectation.
    const camion = modifie.truck_id ? await lireEntite('/trucks', modifie.truck_id) : null;
    const reste = calculerResteApresChangementDeVolume(modifie, await affectationsDuTrajet(modifie.id), camion);
    if (reste !== null) modifie = { ...modifie, remaining_liters: reste };
  }
  const complet = await enregistrerTrajet(modifie, touches);
  if (volumeChange || camionChange) {
    const parId = await trajetsParId();
    parId[String(complet.id)] = complet;
    for (const commande of await lireEntites('/orders')) {
      if ((commande.allocated_trips || []).some((e) => String(e.trip_id) === String(complet.id))) {
        await ecrireEntiteLocale('/orders', commande.id, await recalculerCommande(commande, parId));
        touches.push({ kind: '/orders', id: commande.id });
      }
    }
  }
  if (volumeChange) {
    // Le volume chargé se reflète sur le transit déjà écrit, et sur les commandes affectées.
    const mouvements = await lireEntites('/stock');
    const chargement = mouvements.find((m) => m.trip_id === complet.id && m.source === 'AUTO_TRIP_LOADING');
    const ecritures = [];
    if (chargement) {
      ecritures.push({
        ...chargement,
        quantity_liters: num(complet.fuel_quantity_real),
        total_value: num(complet.fuel_quantity_real) * num(complet.fuel_price_unit),
        updated_at: maintenant(),
      });
    }
    await appliquerEcrituresStock({ ecritures, suppressions: [] }, touches);
  }
  return touches.slice(1);
});

definirEffetLocal('trajet-supprime', async (ctx, { trajet }) => {
  const touches = [];
  if (trajet?.truck_id && !TERMINES.has(String(trajet.status || '').toUpperCase())) {
    const camion = await lireEntite('/trucks', trajet.truck_id);
    if (camion) {
      await ecrireEntiteLocale('/trucks', camion.id, { ...camion, current_status: 'FREE', updated_at: maintenant() });
      touches.push({ kind: '/trucks', id: camion.id });
    }
  }
  return touches;
});

definirEffetLocal('statut-trajet', async (ctx, { tripId, statut, codeAction, note }) => {
  const touches = [];
  const trajet = await lireEntite('/trips', tripId);
  if (!trajet) return touches;
  let nouveauStatut = statut;
  if (!nouveauStatut && codeAction) {
    try {
      const entree = await db.httpCache.get(`/trips/${tripId}/workflow/actions`);
      const actions = entree?.data?.data ?? entree?.data ?? [];
      const action = (Array.isArray(actions) ? actions : []).find((a) => a.action_code === codeAction || a.code === codeAction);
      nouveauStatut = action?.to_step_code || action?.to_status || null;
    } catch (erreur) {
      nouveauStatut = null;
    }
  }
  if (!nouveauStatut || nouveauStatut === trajet.status) return touches;
  const ancien = trajet.status;
  const mouvements = await lireEntites('/stock');
  const gestionDepot = await lireGestionDepot(mouvements);
  const mis = { ...trajet, status: nouveauStatut, updated_at: maintenant() };
  if (nouveauStatut === 'COMPLETED' || nouveauStatut === 'DELIVERED') mis.return_date = mis.return_date || maintenant();
  const complet = await enregistrerTrajet(mis, touches);
  await appliquerEcrituresStock(
    effetsDuStatutSurLeStock({
      trajet: complet,
      ancienStatut: ancien,
      nouveauStatut,
      mouvements,
      managesDepot: gestionDepot,
    }),
    touches,
  );
  // Historique du trajet.
  const suivi = {
    id: nouvelIdentifiant(),
    trip_id: tripId,
    status: nouveauStatut,
    notes: note || null,
    created_at: maintenant(),
  };
  await ecrireEntiteLocale(`/trips/${tripId}/updates`, suivi.id, suivi);
  await marquerDerive(`/trips/${tripId}/updates`, suivi.id);
  touches.push({ kind: `/trips/${tripId}/updates`, id: suivi.id, derive: true });
  // Le camion redevient libre quand son trajet se termine.
  if (TERMINES.has(nouveauStatut) && complet.truck_id) {
    const camion = await lireEntite('/trucks', complet.truck_id);
    if (camion) {
      await ecrireEntiteLocale('/trucks', camion.id, { ...camion, current_status: 'FREE', updated_at: maintenant() });
      touches.push({ kind: '/trucks', id: camion.id });
    }
  }
  // Les commandes affectées voient partir (ou arriver) leur camion.
  const parId = await trajetsParId();
  for (const commande of await lireEntites('/orders')) {
    if ((commande.allocated_trips || []).some((e) => String(e.trip_id) === String(tripId))) {
      await ecrireEntiteLocale('/orders', commande.id, await recalculerCommande(commande, parId));
      touches.push({ kind: '/orders', id: commande.id });
    }
  }
  return touches.slice(1);
});

// --- Stock saisi à la main -----------------------------------------------------------------------

definirEffetLocal('mouvement-stock', async () => []);

// --- Frais ---------------------------------------------------------------------------------------

definirEffetLocal('frais-saisi', async (ctx, { corps }) => {
  const id = corps.id || nouvelIdentifiant();
  const ligne = {
    id,
    amount: num(corps.amount),
    category: corps.category || null,
    kind: corps.kind || 'EXPENSE',
    truck_id: corps.truck_id || null,
    trip_id: corps.trip_id || null,
    operation_id: corps.operation_id || null,
    client_id: corps.client_id || null,
    description: corps.description || corps.label || null,
    date: corps.date || corps.expense_date || maintenant(),
    state: 'POSTED',
    local: true,
    created_at: maintenant(),
  };
  await ecrireEntiteLocale('/finance/expenses', id, ligne);
  await marquerDerive('/finance/expenses', id);
  return [{ kind: '/finance/expenses', id, derive: true }];
});

// --- Entretien ---------------------------------------------------------------------------------

async function recalculerStatutCamion(truckId, touches) {
  if (!truckId) return;
  const camion = await lireEntite('/trucks', truckId);
  if (!camion) return;
  const immobilise = (await lireEntites('/maintenance/immobilizations')).some(
    (i) => i.truck_id === truckId && i.status === 'active',
  );
  let statut = camion.current_status;
  if (immobilise) statut = 'MAINTENANCE';
  else if (camion.current_status === 'MAINTENANCE') {
    const enTrajet = (await lireEntites('/trips')).some(
      (t) => t.truck_id === truckId && !TERMINES.has(String(t.status || '').toUpperCase()),
    );
    statut = enTrajet ? 'ON_TRIP' : 'FREE';
  }
  if (statut !== camion.current_status) {
    await ecrireEntiteLocale('/trucks', camion.id, { ...camion, current_status: statut, updated_at: maintenant() });
    touches.push({ kind: '/trucks', id: camion.id });
  }
}

definirEffetLocal('entretien-plan', async (ctx, { plan }) => {
  const touches = [];
  const releves = dernierReleveParCamion(await lireEntites('/maintenance/readings'));
  const releve = releves[plan.truck_id];
  let complet = { ...plan, is_active: plan.is_active !== false };
  if (complet.last_service_km === undefined || complet.last_service_km === null) {
    complet.last_service_km = ['mileage', 'both'].includes(complet.trigger_type || 'mileage') ? num(releve?.value) : null;
  }
  if (!complet.last_service_date) complet.last_service_date = new Date().toISOString().slice(0, 10);
  complet = { ...complet, ...calculerEcheanceDuPlan(complet) };
  complet = evaluerPlan(complet, releve ? num(releve.value) : null);
  await ecrireEntiteLocale('/maintenance/plans', complet.id, complet);
  return touches;
});

definirEffetLocal('entretien-papier', async (ctx, { papier }) => {
  await ecrireEntiteLocale('/maintenance/documents', papier.id, evaluerPapier({ ...papier, is_active: papier.is_active !== false }));
  return [];
});

definirEffetLocal('entretien-releve', async (ctx, { releve }) => {
  // Un nouveau relevé change l'état des plans du camion.
  const touches = [];
  const derniers = dernierReleveParCamion([...(await lireEntites('/maintenance/readings')), releve]);
  for (const plan of await lireEntites('/maintenance/plans')) {
    if (plan.truck_id !== releve.truck_id) continue;
    const mis = evaluerPlan(plan, derniers[plan.truck_id] ? num(derniers[plan.truck_id].value) : null);
    await ecrireEntiteLocale('/maintenance/plans', plan.id, mis);
    touches.push({ kind: '/maintenance/plans', id: plan.id });
  }
  return touches;
});

definirEffetLocal('entretien-immobilisation', async (ctx, { immobilisation }) => {
  const touches = [];
  await recalculerStatutCamion(immobilisation.truck_id, touches);
  return touches;
});

export { STATUTS_AFFECTABLES };
